import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactPaginate from 'react-paginate';
import useSWR from 'swr';
import { ArrowLeftIcon, ArrowRightIcon } from 'src/components/Icons/icons';
import { TextIcon } from 'src/components/Icons/TextIcon';

import container from 'src/container';
import {
    SASInfo,
    UserRole,
    getPrimaryRole,
    isOrganizationAdministrator,
} from 'src/domain/user';
import { Message } from 'src/domain/message';
import { Consumer } from 'src/domain/consumer';
import { OrganizationGroup } from 'src/domain/organization';
import { AZURE_STORAGE_URL } from 'src/config';
import { useSignedInUser } from 'src/view/hooks/useSignedInUser';
import { useNotification } from 'src/components/Notification/useNotfication';
import { MessageItem, MessageAttachmentView } from './MessageItem';
import { canCloseMessageCommentsForScope } from './messageCommentScope';

const UNREAD_BORDER_DISMISS_DELAY_MS = 5000;

export type MessagesListProps = {
    messages: Message[];
    isLoading: boolean;
    isRefreshing: boolean;
    consumers?: Consumer[];
    organizationGroups?: OrganizationGroup[];
    groupsById?: Map<string, string>;
    leafGroupLabelsById?: Map<string, string>;
    parentGroupLabelsByConsumerId?: Map<string, string>;
    recipientsById?: Map<string, string>;
    senderBadgesByUserId?: Map<
        string,
        {
            label: string;
            className: string;
        }
    >;
    targetLabelKey?: string;
    pageCount: number;
    currentPage: number;
    onPageChange: (page: number) => void;
    onRefreshList?: () => Promise<void> | void;
    refreshAfterVisibleRead?: boolean;
    showCommentToggle?: boolean;
};

export const MessagesList = (props: MessagesListProps) => {
    const {
        messages,
        isLoading,
        consumers,
        organizationGroups,
        groupsById,
        leafGroupLabelsById,
        parentGroupLabelsByConsumerId,
        recipientsById,
        senderBadgesByUserId,
        targetLabelKey,
        pageCount,
        currentPage,
        onPageChange,
        onRefreshList,
        refreshAfterVisibleRead = true,
        showCommentToggle = false,
    } = props;
    const { t } = useTranslation();
    const notify = useNotification();
    const user = useSignedInUser();
    const role = getPrimaryRole(user);
    const [deletingMessageId, setDeletingMessageId] = useState<string | null>(
        null,
    );
    const [togglingMessageId, setTogglingMessageId] = useState<string | null>(
        null,
    );
    const [locallyReadActivityByMessageId, setLocallyReadActivityByMessageId] =
        useState<Map<string, string>>(new Map());
    const readingActivityRef = useRef<Set<string>>(new Set());
    const failedActivityRef = useRef<Set<string>>(new Set());
    const dismissTimerByActivityRef = useRef<
        Map<string, ReturnType<typeof setTimeout>>
    >(new Map());
    const {
        cradle: { munduClient, storageService },
    } = container;

    const organizationId = messages[0]?.organizationId;
    const hasAttachments = useMemo(
        () => messages.some(message => message.attachments?.length),
        [messages],
    );

    const { data: messageMediaSas } = useSWR<SASInfo | undefined>(
        organizationId && hasAttachments
            ? ['messageMediaSas', organizationId]
            : null,
        () => munduClient.getMessageMediaSas(),
        { revalidateOnFocus: false },
    );

    const attachmentsByMessageId = useMemo(() => {
        if (!messageMediaSas) {
            return undefined;
        }

        const map = new Map<string, MessageAttachmentView[]>();

        messages.forEach(message => {
            if (!message.attachments?.length) {
                return;
            }

            if (
                message.attachmentsContainerName &&
                message.attachmentsContainerName !==
                    messageMediaSas.containerName
            ) {
                return;
            }

            const entries = message.attachments.map(attachment => {
                const thumbnailUrl =
                    attachment.thumbnailId &&
                    (!attachment.thumbnailContainerName ||
                        attachment.thumbnailContainerName ===
                            messageMediaSas.containerName)
                        ? `${AZURE_STORAGE_URL}/${messageMediaSas.containerName}/${attachment.thumbnailId}?${messageMediaSas.value}`
                        : undefined;

                const url =
                    storageService.getImageUrl(
                        attachment.id,
                        messageMediaSas,
                    ) ?? '';

                return {
                    ...attachment,
                    url,
                    thumbnailUrl,
                };
            });

            map.set(message.id, entries);
        });

        return map;
    }, [messageMediaSas, messages, storageService]);

    const handleVisibleRead = useCallback(
        async (message: Message) => {
            if (user?.supportMode) return;

            const activityKey = message.unreadActivityAt ?? message.createdDate;
            const readKey = `${message.id}:${activityKey}`;

            if (
                !message.isUnreadByMe ||
                readingActivityRef.current.has(readKey) ||
                failedActivityRef.current.has(readKey)
            ) {
                return;
            }

            readingActivityRef.current.add(readKey);

            try {
                await munduClient.markMessageRead(message.id);
                const dismissTimer = setTimeout(() => {
                    setLocallyReadActivityByMessageId(current => {
                        const next = new Map(current);
                        next.set(message.id, activityKey);
                        return next;
                    });
                    dismissTimerByActivityRef.current.delete(readKey);
                    readingActivityRef.current.delete(readKey);

                    if (refreshAfterVisibleRead && onRefreshList) {
                        void onRefreshList();
                    }
                }, UNREAD_BORDER_DISMISS_DELAY_MS);
                dismissTimerByActivityRef.current.set(readKey, dismissTimer);
            } catch {
                failedActivityRef.current.add(readKey);
                setLocallyReadActivityByMessageId(current => {
                    const next = new Map(current);
                    if (next.get(message.id) === activityKey) {
                        next.delete(message.id);
                    }
                    return next;
                });
                readingActivityRef.current.delete(readKey);
            }
        },
        [
            munduClient,
            onRefreshList,
            refreshAfterVisibleRead,
            user?.supportMode,
        ],
    );

    useEffect(() => {
        const dismissTimers = dismissTimerByActivityRef.current;

        return () => {
            dismissTimers.forEach(timer => {
                clearTimeout(timer);
            });
            dismissTimers.clear();
        };
    }, []);

    if (isLoading) {
        return (
            <p className="text-sm text-mongooseGray">{t('common.loading')}</p>
        );
    }

    if (!messages.length) {
        return (
            <p className="text-sm text-mongooseGray">
                {t('modules.messages.listEmpty')}
            </p>
        );
    }

    const formatter = new Intl.DateTimeFormat(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
    });

    const shouldShowPagination = pageCount > 1;
    const canCloseCommentsForRole =
        !user?.supportMode &&
        showCommentToggle &&
        (isOrganizationAdministrator(user) || role === UserRole.creator);
    const canComment =
        canCloseCommentsForRole ||
        role === UserRole.nextOfKin ||
        role === UserRole.consumer;

    const handlePageChange = ({ selected }: { selected: number }) => {
        onPageChange(selected + 1);
    };

    return (
        <div className="flex flex-col gap-4">
            {messages.map(message => {
                const activityKey =
                    message.unreadActivityAt ?? message.createdDate;
                const suppressUnreadBorder =
                    message.isUnreadByMe &&
                    locallyReadActivityByMessageId.get(message.id) ===
                        activityKey;
                const keepUnreadBorder = dismissTimerByActivityRef.current.has(
                    `${message.id}:${activityKey}`,
                );
                const displayMessage = keepUnreadBorder
                    ? { ...message, isUnreadByMe: true }
                    : suppressUnreadBorder
                    ? {
                          ...message,
                          isUnreadByMe: false,
                      }
                    : message;
                const createdAt = formatter.format(
                    new Date(displayMessage.createdDate),
                );
                const groupLabels = displayMessage.target.groupIds?.map(
                    groupId =>
                        leafGroupLabelsById?.get(groupId) ??
                        groupsById?.get(groupId) ??
                        groupId,
                );
                const attachments = attachmentsByMessageId?.get(
                    displayMessage.id,
                );
                const canDelete = Boolean(
                    !user?.supportMode &&
                        (displayMessage.receiverId
                            ? isOrganizationAdministrator(user)
                            : (user?.id &&
                                  user.id === displayMessage.senderId) ||
                              isOrganizationAdministrator(user)),
                );
                const canEdit = Boolean(
                    !user?.supportMode &&
                        user?.id &&
                        user.id === displayMessage.senderId,
                );
                const canCloseComments =
                    canCloseCommentsForRole &&
                    canCloseMessageCommentsForScope({
                        message: displayMessage,
                        consumers,
                        organizationGroups,
                    });

                const handleDelete = async () => {
                    if (!canDelete || deletingMessageId) return;

                    try {
                        setDeletingMessageId(displayMessage.id);

                        if (messages.length === 1 && currentPage > 1) {
                            onPageChange(currentPage - 1);
                        }

                        await munduClient.deleteMessage(displayMessage.id);

                        if (onRefreshList) {
                            await onRefreshList();
                        }

                        notify(t('modules.messages.deleteSuccess'));
                    } catch (error) {
                        if (error instanceof Error) {
                            notify(error.message, true);
                        } else {
                            notify('Failed to delete message', true);
                        }
                    } finally {
                        setDeletingMessageId(null);
                    }
                };

                const handleCloseComments = async () => {
                    if (!canCloseComments) return;

                    try {
                        setTogglingMessageId(displayMessage.id);
                        await munduClient.updateMessageCommenting(
                            displayMessage.id,
                            {
                                commentsEnabled: false,
                            },
                        );

                        if (onRefreshList) {
                            await onRefreshList();
                        }

                        notify(t('modules.messages.commentsClosedNotice'));
                    } catch (error) {
                        if (error instanceof Error) {
                            notify(error.message, true);
                        } else {
                            notify('Failed to update comments', true);
                        }
                    } finally {
                        setTogglingMessageId(null);
                    }
                };

                return (
                    <MessageItem
                        key={displayMessage.id}
                        message={displayMessage}
                        createdAt={createdAt}
                        groupLabels={groupLabels}
                        parentGroupLabelsByConsumerId={
                            parentGroupLabelsByConsumerId
                        }
                        recipientsById={recipientsById}
                        senderBadge={senderBadgesByUserId?.get(
                            displayMessage.senderId,
                        )}
                        attachments={attachments}
                        canDelete={canDelete}
                        canEdit={canEdit}
                        currentUserId={user?.id}
                        isDeleting={deletingMessageId === displayMessage.id}
                        isTogglingComments={
                            togglingMessageId === displayMessage.id
                        }
                        onDelete={handleDelete}
                        onUpdated={onRefreshList}
                        targetLabelKey={targetLabelKey}
                        canComment={canComment}
                        canCloseComments={canCloseComments}
                        onCloseComments={handleCloseComments}
                        onVisibleRead={handleVisibleRead}
                    />
                );
            })}
            {/* {isRefreshing && (
                <p className="text-xs text-mongooseGray">
                    {t('modules.messages.listRefreshing')}
                </p>
            )} */}
            {shouldShowPagination && (
                <ReactPaginate
                    breakLabel="..."
                    nextLabel={<TextIcon SVG={ArrowRightIcon} />}
                    previousLabel={<TextIcon SVG={ArrowLeftIcon} />}
                    onPageChange={handlePageChange}
                    pageRangeDisplayed={3}
                    marginPagesDisplayed={1}
                    pageCount={pageCount}
                    forcePage={Math.max(0, currentPage - 1)}
                    containerClassName="mt-2 flex flex-wrap items-center justify-center gap-2 text-sm pb-4"
                    pageClassName="rounded-full border border-transparent hover:bg-woodyBrown/10 transition-colors"
                    pageLinkClassName="flex items-center justify-center min-w-[32px] min-h-[32px]"
                    activeClassName="bg-woodyBrown text-lightPeach"
                    previousClassName="rounded-full border border-transparent hover:bg-woodyBrown/10 transition-colors text-woodyBrown"
                    nextClassName="rounded-full border border-transparent hover:bg-woodyBrown/10 transition-colors text-woodyBrown"
                    breakClassName="px-3 py-1 text-mongooseGray"
                    renderOnZeroPageCount={null}
                />
            )}
        </div>
    );
};
